import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { foodService } from '../../../services/foodService';

const FoodVendorProducts = () => {
  const { city, vendor } = useParams();
  const navigate = useNavigate();
  const [vendorData, setVendorData] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadVendorAndProducts();
    loadCart();
  }, [vendor]);

  const loadVendorAndProducts = async () => {
    try {
      setLoading(true);
      
      const vendorInfo = await foodService?.getVendorBySlug(vendor);
      setVendorData(vendorInfo);

      const productsData = await foodService?.getProductsByVendor(vendorInfo?.id);
      setProducts(productsData);
    } catch (err) {
      console.error('Error loading vendor:', err);
      setError('Errore nel caricamento del menu');
    } finally {
      setLoading(false);
    }
  };

  const loadCart = () => {
    const currentCart = foodService?.getCart();
    setCart(currentCart);
  };

  const handleAddToCart = (product) => {
    const cityData = foodService?.getCart()?.cityId || vendorData?.city_id;
    const updatedCart = foodService?.addToCart(product, vendor, cityData);
    setCart(updatedCart);
  };

  const getProductQuantityInCart = (productId) => {
    const item = cart?.items?.find(item => item?.id === productId);
    return item ? item?.quantity : 0;
  };

  const handleGoToCart = () => {
    navigate('/food/cart');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Caricamento menu...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <p className="text-destructive mb-4">{error}</p>
          <button
            onClick={() => navigate(`/food/${city}`)}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Torna agli esercenti
          </button>
        </div>
      </div>
    );
  }

  const cartItemCount = cart?.items?.reduce((sum, item) => sum + item?.quantity, 0);

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="bg-card shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <button
            onClick={() => navigate(`/food/${city}`)}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-4"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Torna agli esercenti</span>
          </button>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="Store" size={32} className="text-primary" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-foreground mb-1">{vendorData?.name}</h1>
              {vendorData?.category && (
                <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm rounded-full mb-2">
                  {vendorData?.category}
                </span>
              )}
              {vendorData?.description_short && (
                <p className="text-muted-foreground">{vendorData?.description_short}</p>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Products Grid */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {products?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="UtensilsCrossed" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Nessun prodotto disponibile</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products?.map((product) => {
              const quantityInCart = getProductQuantityInCart(product?.id);
              return (
                <div
                  key={product?.id}
                  className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-all duration-300"
                >
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-foreground mb-2">{product?.name}</h3>
                    {product?.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{product?.description}</p>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <span className="text-xl font-bold text-primary">€{product?.price?.toFixed(2)}</span>
                    <button
                      onClick={() => handleAddToCart(product)}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                    >
                      <Icon name="Plus" size={18} />
                      <span>Aggiungi</span>
                      {quantityInCart > 0 && (
                        <span className="ml-1 px-2 py-0.5 bg-primary-foreground text-primary rounded-full text-xs font-bold">
                          {quantityInCart}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Floating Cart Button */}
      {cartItemCount > 0 && (
        <div className="fixed bottom-6 left-0 right-0 z-50">
          <div className="max-w-6xl mx-auto px-6">
            <button
              onClick={handleGoToCart}
              className="w-full bg-primary text-primary-foreground rounded-xl px-6 py-4 shadow-2xl hover:bg-primary/90 transition-all duration-300 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-foreground/20 rounded-full flex items-center justify-center">
                  <Icon name="ShoppingCart" size={20} />
                </div>
                <div className="text-left">
                  <div className="font-semibold">Vai al carrello</div>
                  <div className="text-sm opacity-90">{cartItemCount} {cartItemCount === 1 ? 'prodotto' : 'prodotti'}</div>
                </div>
              </div>
              <Icon name="ChevronRight" size={24} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoodVendorProducts;